import { DEFER_COLLAB_SEND } from '@atlaskit/editor-common/collab-defer-collab-send';
import {
	AGENT_ATTRIBUTION_META,
	type AgentAttributionTransactionMeta,
} from '@atlaskit/editor-common/transaction-agent-attribution';
import type { Slice } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { purePmInEditorDirectStreamingPluginKey } from './pluginKey';
import type { CellKindConversionState } from './types';

/**
 * Original actions - unchanged enum values
 */
export enum ACTIONS {
	INIT_POSITIONS = 'INIT_POSITIONS',
	ADJUST_INIT_POSITIONS = 'ADJUST_INIT_POSITIONS',
	UPDATE_LOADING_POSITIONS = 'UPDATE_LOADING_POSITIONS',
	UPDATE_STREAM_POSITIONS = 'UPDATE_STREAM_POSITIONS',
	END_STREAM = 'END_STREAM',
	SET_USER_HAS_SCROLLED_IN_SESSION = 'SET_USER_HAS_SCROLLED_IN_SESSION',
}

/**
 * Initialize positions and start streaming
 */
export const initPositions =
	({
		start,
		end,
		showHighlight,
		showAIContentHighlight,
		showTelepointer,
		telepointerPos,
		isQuickAction,
		originalUserContentSlice,
		cellKindConversion,
		streamStartPos,
		sessionId,
	}: {
		cellKindConversion?: CellKindConversionState;
		end: number;
		isQuickAction?: boolean;
		originalUserContentSlice?: Slice;
		sessionId?: string;
		showAIContentHighlight: boolean;
		showHighlight: boolean;
		showTelepointer: boolean;
		start: number;
		streamStartPos?: number;
		telepointerPos: number;
	}) =>
	(tr: Transaction): Transaction => {
		return tr.setMeta(purePmInEditorDirectStreamingPluginKey, {
			action: ACTIONS.INIT_POSITIONS,
			start,
			end,
			showHighlight,
			showAIContentHighlight,
			showTelepointer,
			telepointerPos,
			isQuickAction,
			originalUserContentSlice,
			...(cellKindConversion && { cellKindConversion }),
			streamStartPos,
			sessionId,
		});
	};

/**
 * Adjust initial positions
 */
export const adjustInitPositions =
	({ start, end }: { end: number; start: number }) =>
	(tr: Transaction): Transaction => {
		return tr.setMeta(purePmInEditorDirectStreamingPluginKey, {
			action: ACTIONS.ADJUST_INIT_POSITIONS,
			start,
			end,
		});
	};

/**
 * Update loading animation position
 */
export const updateLoadingPositions = (tr: Transaction): Transaction => {
	return tr.setMeta(purePmInEditorDirectStreamingPluginKey, {
		action: ACTIONS.UPDATE_LOADING_POSITIONS,
	});
};

/**
 * Update stream positions after content change
 */
export const updateStreamPositions =
	({
		aiContentEndPos,
		telepointerPos,
		isFinalStream,
		lastProcessedVersion,
		aiContentSlice,
		scrollTargetPos,
		posAdjusted,
		startPos,
		userContentEndPos,
		collabFlushedBlockCount,
		cellKindConversion,
	}: {
		aiContentEndPos: number;
		aiContentSlice?: Slice;
		cellKindConversion?: CellKindConversionState;
		collabFlushedBlockCount?: number;
		isFinalStream: boolean;
		lastProcessedVersion?: number;
		posAdjusted?: boolean;
		scrollTargetPos?: number;
		startPos?: number;
		telepointerPos: number;
		userContentEndPos?: number;
	}) =>
	(tr: Transaction): Transaction => {
		return tr.setMeta(purePmInEditorDirectStreamingPluginKey, {
			action: ACTIONS.UPDATE_STREAM_POSITIONS,
			positions: {
				aiContentEndPos,
				telepointerPos,
				isFinalStream,
				lastProcessedVersion,
				aiContentSlice,
				scrollTargetPos,
				posAdjusted,
				startPos,
				userContentEndPos,
				collabFlushedBlockCount,
				...(cellKindConversion !== undefined && { cellKindConversion }),
			},
		});
	};

/**
 * End stream and reset state
 */
export const endStream = (tr: Transaction): Transaction => {
	// Every way a stream can end routes through here, including the error cleanup and midstream
	// discard paths that never reach finalize. Releasing the collab send hold unconditionally means
	// queued steps — the stream's own, and any local edit sitting behind them — are sent on this
	// transaction rather than waiting for the hold to lapse on some later, unrelated state update.
	// Redundant on the finalize path, where restore and commit already release it.
	return tr.setMeta(DEFER_COLLAB_SEND, false).setMeta(purePmInEditorDirectStreamingPluginKey, {
		action: ACTIONS.END_STREAM,
	});
};

/**
 * Meta transaction helper for streaming transformations
 */
export const metaTransaction = ({
	tr,
	isFinalStream,
	stackTime,
	agentAttribution,
}: {
	agentAttribution?: AgentAttributionTransactionMeta;
	isFinalStream: boolean;
	stackTime: number | undefined;
	tr: Transaction;
}): Transaction => {
	tr = tr
		.setMeta('isAIStreamingTransformation', true)
		.setMeta('isAIFinalStreamingTransformation', isFinalStream)
		.setMeta('scrollIntoView', false);
	if (fg('platform_editor_agentic_step_attribution') && agentAttribution) {
		tr.setMeta(AGENT_ATTRIBUTION_META, agentAttribution);
	}
	if (stackTime) {
		tr.setTime(stackTime);
	}
	return tr;
};
